import React, {
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ModalBodyStyled,
  ModalCloseButtonStyled,
  ModalCloseIconStyled,
  ModalContentStyled,
  ModalWrapperStyled,
} from './Modal.styled';

const Modal: React.FC<{
  modalButtonElement: (props: {}) => JSX.Element;
  ariaLabel?: string;
}> = ({ children, modalButtonElement, ariaLabel }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const modalButtonRef = useRef<HTMLButtonElement>(null);

  const toggleModal = useCallback(() => {
    // reset focus
    if (isOpen) modalButtonRef.current?.focus();

    toggleAriaHidden(!isOpen);
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen, modalButtonRef]);

  // trap focus inside the modal
  useEffect(() => {
    if (!isOpen) return;

    const focusableEls = modalRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );

    const trap = (e: KeyboardEvent) => {
      if (!focusableEls) {
        e.preventDefault();
        return;
      }
      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
        return;
      }

      if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', trap);

    return () => document.removeEventListener('keydown', trap);
  }, [isOpen]);

  // close modal on escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggleModal();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggleModal]);

  const handleOnClick = (e: MouseEvent<HTMLDivElement>) => {
    if (!isOpen) return;
    if (modalRef.current?.contains(e.target as Node)) return;

    e.preventDefault();
    toggleModal();
  };

  const toggleAriaHidden = (isOpen: boolean) => {
    document
      ?.getElementById('root')
      ?.setAttribute('aria-hidden', String(isOpen));
    // the SSL logo gets added in index.html outside of #root so it needs to be hidden seperatly here
    document
      ?.getElementById('positiveSslLogo')
      ?.setAttribute('aria-hidden', String(isOpen));
  };

  const ModalComp = createPortal(
    <ModalWrapperStyled
      onClick={handleOnClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-testid="modal-wrapper"
    >
      <ModalBodyStyled ref={modalRef}>
        <ModalCloseButtonStyled
          onClick={toggleModal}
          title="close"
          autoFocus={true}
          data-cy="modal-close-button"
          data-testid="modal-close-button"
        >
          <ModalCloseIconStyled name="TimesLight" size="0.875rem" />
        </ModalCloseButtonStyled>
        <ModalContentStyled>{children}</ModalContentStyled>
      </ModalBodyStyled>
    </ModalWrapperStyled>,
    document.body,
  );

  return (
    <>
      {modalButtonElement({ onClick: toggleModal, ref: modalButtonRef })}
      {isOpen && ModalComp}
    </>
  );
};

export default Modal;
